import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import AvatarOrInitial from '../Family/AvatarOrInitial';
import { Send, MessageSquare, Calendar, X, Flag, Check, User } from 'lucide-react';

export default function FrictionlessMessageComposer({ 
  onSend, 
  onCancel,
  autoFocus = true,
  placeholder = "Don't forget class runs late tonight, need to bring donuts to class Wednesday, tomorrow's the last day to sign up for camp...",
  initialRecipients = [],
  showRecipientSelector = true,
  className = ""
}) {
  const { family, currentUser } = useFamilyContext();
  const [content, setContent] = useState('');
  const [recipients, setRecipients] = useState(initialRecipients);
  const [isUrgent, setIsUrgent] = useState(false);
  const [dueDate, setDueDate] = useState('');
  const [isSending, setIsSending] = useState(false);
  
  const textareaRef = useRef(null);

  // All family members for selection + self option
  const allMembers = [
    ...(family?.parents || []),
    ...(family?.kids || [])
  ].filter(member => member.id !== currentUser?.id);

  // Add "Note to Self" option
  const selfOption = {
    id: currentUser?.id,
    name: 'Note to Self',
    avatar: currentUser?.avatar || null,
    role: 'self'
  };

  const allRecipientOptions = [selfOption, ...allMembers];

  // Auto-focus and auto-resize
  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      const timer = setTimeout(() => {
        textareaRef.current.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [autoFocus]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [content]);



  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e) => {
    // Send on Cmd/Ctrl + Enter
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
    
    // Escape to cancel
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  }, [content, recipients]);

  // Send message
  const handleSend = async () => {
    if (!content.trim() || recipients.length === 0 || isSending) return;

    setIsSending(true);
    try {
      // Handle "Note to Self" differently
      const isSelfNote = recipients.some(r => r.role === 'self');
      
      if (isSelfNote) {
        await onSend({
          content: content.trim(),
          recipientIds: [currentUser.id],
          priority: isUrgent ? 'high' : 'normal',
          type: 'personal_note',
          dueDate: dueDate || undefined
        });
      } else {
        await onSend({
          content: content.trim(),
          recipientIds: recipients.map(r => r.id),
          priority: isUrgent ? 'high' : 'normal',
          type: 'reminder', // Use 'reminder' type for task integration
          dueDate: dueDate || undefined
        });
      }
      
      // Reset form
      setContent('');
      setRecipients(initialRecipients);
      setIsUrgent(false);
      setDueDate('');
      
      // Keep focus for continuous messaging
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsSending(false);
    }
  };

  // Cancel composition
  const handleCancel = () => {
    setContent('');
    setRecipients(initialRecipients);
    setIsUrgent(false);
    setDueDate('');
    if (onCancel) onCancel();
  };

  // Toggle recipient selection
  const toggleRecipient = (member) => {
    const isSelected = recipients.find(r => r.id === member.id);
    if (isSelected) {
      setRecipients(recipients.filter(r => r.id !== member.id));
    } else {
      setRecipients([...recipients, member]);
    }
  };



  // Get selected recipient names for "To" indicator
  const getSelectedNames = () => {
    if (recipients.length === 0) return 'Select recipients above';
    return recipients.map(r => r.name).join(', ');
  };

  const canSend = content.trim() && recipients.length > 0 && !isSending;

  return (
    <div className={`bg-white ${className}`}>
      {/* Composer Header */}
      <div className="px-6 pt-6 pb-5">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 bg-gradient-to-br from-magenta-500 to-magenta-600 rounded-lg flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-900 mb-0.5">Send a Reminder</h2>
            <p className="text-xs font-medium text-stone-500">Add to someone's to-do list</p>
          </div>
        </div>
      </div>
     
      {/* Avatar-Only Recipients Section */}
      {showRecipientSelector && (
        <div className="px-6 pb-5">
          <div className="text-sm font-semibold text-slate-900 mb-3">Send to:</div>
          
          {/* Large Avatar Selection */}
          <div className="flex gap-3 flex-wrap mb-3">
            {allRecipientOptions.map(member => {
              const isSelected = recipients.find(r => r.id === member.id);
              return (
                <button
                  key={member.id}
                  onClick={() => toggleRecipient(member)}
                  className={`relative w-14 h-14 rounded-full transition-all duration-200 transform hover:scale-105 ${
                    isSelected
                      ? 'ring-4 ring-magenta-500 ring-opacity-50 shadow-lg'
                      : 'ring-2 ring-transparent hover:ring-stone-300'
                  }`}
                  style={{ minWidth: '56px', minHeight: '56px' }}
                >
                  {member.role === 'self' ? (
                    <AvatarOrInitial
                      avatar={currentUser?.avatar}
                      name={currentUser?.name || 'You'}
                      size="2xl"
                    />
                  ) : (
                    <AvatarOrInitial
                      avatar={member.avatar}
                      name={member.name}
                      size="2xl"
                    />
                  )}
                  {isSelected && (
                    <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-magenta-500 rounded-full flex items-center justify-center border-2 border-white">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          
          {/* "To" Indicator */}
          <div className={`px-3 py-2 rounded-lg border transition-all duration-200 ${
            recipients.length > 0
              ? 'bg-magenta-50 border-magenta-200 text-magenta-700'
              : 'bg-stone-50 border-stone-200 text-stone-600'
          }`}>
            <span className="text-sm font-medium">
              <span className="font-semibold">To:</span> {getSelectedNames()}
            </span>
          </div>
        </div>
      )}

      {/* Message Input */}
      <div className="px-6 pb-5">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full min-h-[120px] p-4 border-2 border-stone-200 rounded-xl text-base resize-none transition-all duration-150 focus:outline-none focus:border-magenta-500 focus:shadow-sm focus:shadow-magenta-500/10 placeholder-stone-400"
          disabled={isSending}
          style={{ fontSize: '16px' }} // Prevent zoom on iOS
        />
      </div>

      {/* Date & Flag Section */}
      <div className="px-6 pb-5">
        <div className="flex items-center gap-3">
          <div className="flex-1 relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-400 w-4 h-4 pointer-events-none" />
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border-2 border-stone-200 rounded-lg text-base focus:outline-none focus:border-magenta-500 transition-all duration-150 bg-white"
              style={{ minHeight: '48px', fontSize: '16px' }}
              min={new Date().toISOString().split('T')[0]}
              placeholder="Date (optional)"
            />
          </div>
          
          {/* Flag Button */}
          <button
            onClick={() => setIsUrgent(!isUrgent)}
            className={`w-12 h-12 border-2 rounded-lg transition-all duration-150 flex items-center justify-center ${
              isUrgent
                ? 'border-red-500 bg-red-500 text-white shadow-md'
                : 'border-stone-200 text-stone-400 hover:text-red-500 hover:border-red-300 bg-white'
            }`}
            title={isUrgent ? 'Remove urgent flag' : 'Mark as urgent'}
          >
            <Flag className="w-5 h-5" />
          </button>
        </div>
        
        {/* Status Indicators */}
        <div className="flex items-center gap-4 mt-3">
          {dueDate && (
            <span className="text-xs text-magenta-600 font-medium flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {new Date(dueDate).toLocaleDateString()}
            </span>
          )}
          {isUrgent && (
            <span className="text-xs text-red-600 font-medium flex items-center gap-1">
              <Flag className="w-3 h-3" />
              Urgent
            </span>
          )}
        </div>
      </div>

      {/* Send Button */}
      <div className="px-6 pb-6">
        <button
          onClick={handleSend}
          disabled={!canSend}
          className={`w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-base font-semibold transition-all duration-150 ${
            canSend
              ? 'bg-magenta-500 text-white hover:bg-magenta-600 shadow-sm hover:shadow-md'
              : 'bg-stone-200 text-stone-400 cursor-not-allowed'
          }`}
          style={{ minHeight: '52px' }}
        >
          {isSending ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Sending...</span>
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              <span>Send Reminder</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}